import {makeObservable, observable, action, ObservableMap, toJS} from 'mobx';
import {differenceInDays} from 'date-fns';
import {CalendarDay} from '../../ui/components/form/CalendarSelect/CalendarSelectInput';
import {ScheduleRepeatInterval} from "../../ui/elements/ScheduleSetting/ScheduleRepeatTask";
import {CustomInterval} from "../../ui/elements/ScheduleSetting/CustomIntervalInput";
import {getLogicalDay} from "../../ui/screens/Lists/components/utils";


export interface ExceptionData {
    doWeWork: boolean;
    updatedAt: string;
    updatedBy?: string;
}


export class TaskSchedule {
    scheduleStartDate: Date;
    scheduleEndDate?: Date;
    repeatOption: ScheduleRepeatInterval = ScheduleRepeatInterval.EVERY_DAY;
    selectedWeekDays: number[] = []; // 0 = Sunday, 6 = Saturday
    selectedMonthDates: number[] = []; // [1–31]
    selectedYearDates: CalendarDay[] = []; // month = 0-based
    customInterval: CustomInterval = {work: 1, rest: 0};
    exceptions: ObservableMap<string, ExceptionData> = observable.map();
    currentUserId?: string;

    constructor(timezone: string, logicalDayShift: number = 4, rule?: any, currentUserId?: string) {
        const logicalDate = getLogicalDay(new Date(), timezone, logicalDayShift);

        this.scheduleStartDate = new Date(logicalDate.getFullYear(), logicalDate.getMonth(), logicalDate.getDate());
        this.currentUserId = currentUserId;

        if (rule) {
            if (rule.startDate) this.scheduleStartDate = new Date(rule.startDate);
            if (rule.endDate) this.scheduleEndDate = new Date(rule.endDate);
            if (rule.repeatOption !== undefined) this.repeatOption = rule.repeatOption;
            if (rule.weekDays) this.selectedWeekDays = rule.weekDays;
            if (rule.monthDates) this.selectedMonthDates = rule.monthDates;
            if (rule.yearDates) this.selectedYearDates = rule.yearDates;
            if (rule.customInterval) this.customInterval = rule.customInterval;
            if (rule.exceptions) {
                for (const [key, val] of Object.entries(rule.exceptions as Record<string, ExceptionData>)) {
                    this.exceptions.set(key, val);
                }
            }
        }

        makeObservable(this, {
            scheduleStartDate: observable,
            scheduleEndDate: observable,
            repeatOption: observable,
            selectedWeekDays: observable,
            selectedMonthDates: observable,
            selectedYearDates: observable,
            customInterval: observable,
            exceptions: observable,

            setScheduleStartDate: action,
            setScheduleEndDate: action,
            setRepeatOption: action,
            setSelectedWeekDays: action,
            setSelectedMonthDates: action,
            setSelectedYearDates: action,
            setCustomInterval: action,

            toggleWeekDay: action,
            toggleAllWeekDays: action,
            toggleException: action,
            setExceptions: action,
            removeException: action,
            cleanupExceptions: action,
        });
    }

    setScheduleStartDate = (date: Date) => {
        this.scheduleStartDate = date;
        this.cleanupExceptions();
    };
    setScheduleEndDate = (date?: Date) => {
        this.scheduleEndDate = date;
        this.cleanupExceptions();
    };
    setRepeatOption = (opt: ScheduleRepeatInterval) => {
        this.repeatOption = opt;
        this.cleanupExceptions();
    };
    setSelectedWeekDays = (days: number[]) => {
        this.selectedWeekDays = days.splice(0).sort((a, b) => a - b);
        this.cleanupExceptions();
    };

    toggleWeekDay = (dayIndex: number) => {
        const dayIndexPositionInArray = this.selectedWeekDays.indexOf(dayIndex);
        if (dayIndexPositionInArray === -1) {
            this.selectedWeekDays = [...this.selectedWeekDays, dayIndex].sort((a, b) => a - b);
        } else {
            this.selectedWeekDays = this.selectedWeekDays.filter(d => d !== dayIndex);
        }
        this.cleanupExceptions();
    };
    toggleAllWeekDays = (checked: boolean) => {
        if (checked) {
            this.selectedWeekDays = [0, 1, 2, 3, 4, 5, 6];
        } else {
            this.selectedWeekDays = [];
        }
        this.cleanupExceptions();
    }

    static getDateKey(year: number, month: number, day: number) {
        return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    toggleException = (year: number, month: number, day: number, doWeWork: boolean) => {
        const key = TaskSchedule.getDateKey(year, month, day);
        const exists = this.exceptions.get(key);
        if (exists && exists.doWeWork === doWeWork) {
            this.exceptions.delete(key);
        } else {
            this.exceptions.set(key, {
                doWeWork: doWeWork,
                updatedAt: new Date().toISOString(),
                updatedBy: this.currentUserId,
            });
        }
    }

    setExceptions = (newVal: Map<string, ExceptionData>) => {
        this.exceptions = observable.map(newVal);
    };

    removeException = (key: string) => {
        this.exceptions.delete(key);
    };

    cleanupExceptions = () => {
        for (const [key, val] of Array.from(this.exceptions.entries())) {
            const [y, m, d] = key.split('-');
            const year = parseInt(y, 10);
            const month = parseInt(m, 10) - 1;
            const day = parseInt(d, 10);
            if (this.isWorkingByRule(year, month, day) === val.doWeWork) {
                this.exceptions.delete(key);
            }
        }
    };

    setSelectedMonthDates = (days: number[]) => {
        this.selectedMonthDates = days;
        this.cleanupExceptions();
    };
    setSelectedYearDates = (days: CalendarDay[]) => {
        this.selectedYearDates = days;
        this.cleanupExceptions();
    };
    setCustomInterval = (interval: { work: number; rest: number }) => {
        this.customInterval = interval;
        this.cleanupExceptions();
    };

    matchRepeatRule(date: Date) {
        switch (this.repeatOption) {
            case ScheduleRepeatInterval.EVERY_DAY:
                return true;
            case ScheduleRepeatInterval.EVERY_WEEK: {
                const day = date.getDay();
                return this.selectedWeekDays.includes(day);
            }
            case ScheduleRepeatInterval.EVERY_OTHER_WEEK: {
                const diff = differenceInDays(date, this.scheduleStartDate);
                if (Math.floor(diff / 7) % 2 !== 0) return false;
                const day = date.getDay();
                return this.selectedWeekDays.includes(day);
            }
            case ScheduleRepeatInterval.EVERY_MONTH: {
                const day = date.getDate();
                const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
                if (this.selectedMonthDates.length) {
                    if (this.selectedMonthDates.includes(day)) {
                        return true;
                    }
                    if (day === lastDay) {
                        return this.selectedMonthDates.some(d => d > lastDay);
                    }
                }
                return false;
            }
            case ScheduleRepeatInterval.EVERY_YEAR: {
                if (this.selectedYearDates.length) {
                    return this.selectedYearDates.some(d => d.day === date.getDate() && d.month === date.getMonth());
                }
                return (
                    date.getDate() === this.scheduleStartDate.getDate() &&
                    date.getMonth() === this.scheduleStartDate.getMonth()
                );
            }
            case ScheduleRepeatInterval.CUSTOM: {
                const {work, rest} = this.customInterval;
                const diff = differenceInDays(date, this.scheduleStartDate);
                if (diff < 0) return false;
                const cycle = work + rest;
                return cycle > 0 && diff % cycle < work;
            }
            default:
                return false;
        }
    }

    isWorkingByRule(year: number, month: number, day: number): boolean {
        const date = new Date(year, month, day);
        if (date < this.scheduleStartDate) return false;
        if (this.scheduleEndDate && date > this.scheduleEndDate) return false;
        return this.matchRepeatRule(date);
    }

    getScheduledDays(year: number, month: number, withExceptions: boolean = false): number[] {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const result: number[] = [];
        for (let day = 1; day <= daysInMonth; day++) {
            if (this.isDayIncluded(year, month, day, withExceptions)) {
                result.push(day);
            }
        }
        return result;
    }

    isDayIncluded(year: number, month: number, day: number, withExceptions: boolean = false) {
        const date = new Date(year, month, day);
        if (date >= this.scheduleStartDate
            && (!this.scheduleEndDate || date <= this.scheduleEndDate)
        ) {
            const isScheduled = this.matchRepeatRule(date);
            let isExcluded = false;
            let isForced = false;
            if (withExceptions) {
                const dayKey = TaskSchedule.getDateKey(year, month, day);
                if (this.exceptions.has(dayKey)) {
                    isForced = this.exceptions.get(dayKey)!.doWeWork;
                    isExcluded = !this.exceptions.get(dayKey)!.doWeWork;
                }
            }
            return (isScheduled && !isExcluded || isForced);
        }
        return false;

    }

    get scheduleRule() {
        return {
            startDate: this.scheduleStartDate.toISOString(),
            endDate: this.scheduleEndDate ? this.scheduleEndDate.toISOString() : undefined,
            repeatOption: this.repeatOption,
            weekDays: this.selectedWeekDays,
            monthDates: this.selectedMonthDates,
            yearDates: this.selectedYearDates,
            customInterval: this.customInterval,
            exceptions: Object.fromEntries(toJS(this.exceptions)),
        };
    }
}
